import type { Routine } from '../core/types/catalog'

/**
 * Protocollen = de manier waarop Sarah in de salon adviseert: geen los potje,
 * maar een complete routine. Geen bundelkorting (leverancier staat dat niet
 * toe) — de waarde zit in de samenstelling, het huidplan en de begeleiding.
 *
 * Welke klacht bij welk protocol hoort, staat in concerns.ts.
 * De prijs wordt altijd berekend uit de losse producten, nooit opgeslagen.
 */
export const routines: Routine[] = [
  {
    id: 'pigment-protocol',
    name: 'Pigment Protocol',
    claim: 'Egale teint, in de juiste volgorde',
    story:
      'Pigment houdt zich alleen rustig als reiniging, vitamine C en bescherming samen optrekken. De Spirulina Toner helpt daarbij en maakt je huid ontvankelijk voor het serum. Dit is exact wat ik meegeef na een vitamine C behandeling.',
    forWho: 'Vlekken na de zomer, zwangerschap of de overgang',
    productIds: [
      'emerginc-kombucha-cleanser',
      'spirulina-toner',
      'emerginc-vitamine-c-serum',
      'emerginc-sun-spf-50-tinted',
    ],
    included: ['Persoonlijk huidplan in Mijn omgeving', 'Opbouwschema voor de eerste zes weken'],
    treatmentSlug: 'vitamine-c-behandeling',
    level: 'Start',
    lasts: { minMonths: 3, maxMonths: 3 },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/f7cf3984-bb1e-4d69-ac92-ebab86218044.jpg',
  },
  {
    id: 'hormonale-balans',
    name: 'Hormonale Balans Protocol',
    claim: 'Rust in een huid die meebeweegt met je cyclus',
    story:
      'Ontstekingen remmen zonder je huid uit te drogen of je barrière te slopen. Dat laatste is de fout die ik het vaakst zie. De activator bevat glycolzuur, dus een minerale SPF hoort er elke ochtend bij.',
    forWho: 'Onzuiverheden rond kaaklijn en kin, vette of gecombineerde huid',
    productIds: [
      'clarifying-foam',
      'medik8-niacinamide-peptides',
      'balance-moisturizer-glycolic-acid-activator',
      'emerginc-sun-spf-50-tinted',
    ],
    included: ['Persoonlijk huidplan in Mijn omgeving', 'Cyclus-schema: wanneer je wat gebruikt'],
    treatmentSlug: 'get-the-red-out',
    level: 'Start',
    lasts: { minMonths: 3, maxMonths: 3 },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/3dd6c736-8043-474f-8218-fbda6d0364ae.jpg',
  },
  {
    id: 'rust-herstel',
    name: 'Rust & Herstel Protocol',
    claim: 'Eerst kalmeren, dan pas behandelen',
    story:
      'Een reactieve huid heeft niets aan meer actieve stoffen. Zachte reiniging, vitamine B5 en een crème met alfa-liponzuur brengen de roodheid terug, een minerale SPF houdt hem weg. Pas als je huid rustig is, bouwen we verder.',
    forWho: 'Roodheid, rosacea of couperose, een huid die snel reageert',
    productIds: [
      'emerginc-aromatic-cream-tea-cleanser',
      'medik8-hydr8-b5',
      'emerginc-hyper-vitalizer-cream',
      'emerginc-sun-spf-50-tinted',
    ],
    included: ['Persoonlijk huidplan in Mijn omgeving', 'Lijstje: wat je deze weken beter laat staan'],
    treatmentSlug: 'get-the-red-out',
    level: 'Start',
    lasts: { minMonths: 3, maxMonths: 3 },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/3acaccfd-ae65-4406-9300-68b1ed151f6d.jpg',
  },
  {
    id: 'vocht-barriere',
    name: 'Vocht & Barrière Protocol',
    claim: 'Een huid die niet meer trekt',
    story:
      'Een droge huid mist vocht én vet. Een reinigingsolie die je barrière laat zitten, een toner tegen het uitdrogende kraanwater, hyaluronzuur in meerdere lagen en een crème met ceramiden die alles vasthoudt.',
    forWho: 'Droge, schilferige of trekkerige huid, vooral in herfst en winter',
    productIds: [
      'lipid-balance-cleansing-oil',
      'medik8-daily-refreshing-toner',
      'medik8-hydrate-b5-intense',
      'total-moisture-daily-facial-cream',
    ],
    included: ['Persoonlijk huidplan in Mijn omgeving', 'Wintertips voor van binnenuit'],
    level: 'Start',
    lasts: { minMonths: 3, maxMonths: 3 },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/68f3217c-842b-438f-8382-727505107f0f.jpg',
  },
  {
    id: 'bindweefsel-thuis',
    name: 'Bindweefsel Thuisprotocol',
    claim: 'Houd het salonresultaat vast tussen twee afspraken',
    story:
      'Tussen twee afspraken zitten drie weken. Dit protocol zorgt dat je huid in die weken doorwerkt in plaats van terugzakt: een zacht exfoliërende toner, vitamine C, en de koudgeperste olie die ik altijd meegeef.',
    forWho: 'Iedereen in een bindweefselkuur of met een doffe huid',
    productIds: ['emerginc-peach-toner', 'emerginc-vitamine-c-serum', 'rawceuticals-facial-oil'],
    included: ['Persoonlijk huidplan in Mijn omgeving', 'Uitleg gua sha-beweging op video'],
    treatmentSlug: 'bindweefselmassage-los',
    level: 'Start',
    lasts: { minMonths: 3, maxMonths: 3 },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/0ed04ad3-0600-47a3-bc34-389527c1fbf3.jpg',
  },
  {
    id: 'compleet-anti-ageing',
    name: 'Compleet Anti-ageing Protocol',
    claim: 'Alles wat je huid nodig heeft, ochtend en avond',
    story:
      'Het volledige protocol dat ik opstel voor wie serieus met veroudering aan de slag gaat: een reiniging die je barrière spaart, retinal bij nacht, peptiden tegen lijntjes, een verstevigende crème, en overdag vitamine C met SPF in één stap. Vier tot vijf maanden gebruik.',
    forWho: 'Verslapping, lijntjes en een huid in of na de overgang',
    productIds: [
      'lipid-balance-cleansing-oil',
      'medik8-chrystal-retinal-1',
      'medik8-liquid-peptides-advanced-mp',
      'medik8-advanced-pro-collagen-peptide-cream',
      'medik8-daily-radiance-vitamine-c',
    ],
    included: [
      'Persoonlijk huidplan in Mijn omgeving',
      'Videoconsult van 20 minuten met Sarah',
      'Reisformaat Vitamin C serum cadeau',
      'Retinal-opbouwschema van acht weken',
    ],
    treatmentSlug: 'goddess-lift',
    level: 'Compleet',
    lasts: { minMonths: 4, maxMonths: 5 },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/b7214e87-07a3-4d08-a56e-436e377d0b7a.jpg',
  },
]
